/**
 * DailyBriefing — Operations · Daily Briefing (Iter 134)
 *
 * Renders the AI-narrated + metric-rich morning briefing. Uses
 * window.print() with print-specific styles for the "Print PDF" flow.
 * The endpoint caches per-day so refresh is a no-op unless the admin
 * explicitly clicks "Regenerate".
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkle, Printer, ArrowsClockwise, Target, ChartLineUp, Calendar,
  Warning, Wallet, Trophy, Wrench, CircleNotch,
} from "@phosphor-icons/react";

const inr = (n) =>
  "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 })
    .format(Number(n || 0));

export default function DailyBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRegenerating(true); else setLoading(true);
    try {
      const r = await api.get("/daily-briefing", { params: refresh ? { refresh: true } : {} });
      setBriefing(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setLoading(false); setRegenerating(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const print = () => {
    // Print-specific CSS hides the app chrome; see index.css for @media print.
    document.body.classList.add("printing-briefing");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-briefing"), 300);
  };

  return (
    <AppShell title="Daily Briefing" subtitle="Operations · AI morning sync"
      actions={
        <div className="flex gap-2 no-print">
          <button onClick={() => load(true)} disabled={regenerating}
                  data-testid="brief-regen"
                  className="h-10 px-3 rounded-md border border-[#E5E2D9] text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <ArrowsClockwise size={12} className={regenerating ? "animate-spin" : ""}/>
            {regenerating ? "Regenerating…" : "Regenerate"}
          </button>
          <button onClick={print}
                  data-testid="brief-print"
                  className="h-10 px-3 rounded-md bg-[#1F3A57] hover:bg-[#26476A] text-white text-sm inline-flex items-center gap-2">
            <Printer size={12} weight="fill"/>Print PDF
          </button>
        </div>
      }>
      {loading && !briefing ? (
        <div className="flex items-center justify-center h-64 text-[#8A8780] text-sm">
          <CircleNotch size={16} className="animate-spin mr-2"/>Loading…
        </div>
      ) : briefing ? (
        <BriefingSheet briefing={briefing}/>
      ) : null}
    </AppShell>
  );
}

function BriefingSheet({ briefing }) {
  const { narrative, metrics } = briefing;
  return (
    <div className="space-y-4 printable" data-testid="briefing-sheet">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={14} weight="fill" className="text-[#C25D38]"/>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            Rigo AI · Daily Briefing · <b>{briefing.date}</b>
            {briefing.narrative_status === "fallback" && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-[#F9E4B7] text-[#7A5A11] normal-case tracking-normal text-[10px]">
                Fallback (LLM unavailable)
              </span>
            )}
          </div>
        </div>
        <div className="text-lg font-semibold text-[#1A1A1A] leading-snug"
             data-testid="brief-mission">
          {narrative?.mission_line || "Today's mission — focus on scheduled pickups."}
        </div>
      </div>

      <Section icon={<Target size={12}/>} label="Today's Mission" testid="brief-section-mission">
        <Grid>
          <Tile label="Pickups target"  value={metrics.today_plan?.pickups_total}/>
          <Tile label="Outstanding dues (collection target)" value={inr(metrics.finance?.outstanding_dues)}/>
          <Tile label="High-priority tickets" value={metrics.today_plan?.high_priority_tickets}/>
          <Tile label="Staff active" value={metrics.today_plan?.staff_active}/>
        </Grid>
      </Section>

      <Section icon={<ChartLineUp size={12}/>} label="Yesterday's Performance" testid="brief-section-yesterday">
        <Prose>{narrative?.yesterday_summary}</Prose>
        <Grid>
          <Tile label="Pickups done"  value={metrics.yesterday?.pickups_completed}/>
          <Tile label="Missed"        value={metrics.yesterday?.pickups_missed} tone="warn"/>
          <Tile label="New customers" value={metrics.yesterday?.new_customers}/>
          <Tile label="Revenue"       value={inr(metrics.yesterday?.revenue_collected)} tone="ok"/>
          <Tile label="Expenses"      value={inr(metrics.yesterday?.expenses)}/>
          <Tile label="Tickets new"   value={metrics.yesterday?.tickets_new}/>
          <Tile label="Tickets resolved" value={metrics.yesterday?.tickets_resolved}/>
        </Grid>
      </Section>

      <Section icon={<Calendar size={12}/>} label="Today's Schedule" testid="brief-section-today">
        <Prose>{narrative?.today_summary}</Prose>
        {metrics.today_plan?.routes?.length > 0 ? (
          <div className="mt-2 border border-[#F0EEE6] rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#FAF8F2] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
                <tr>
                  <th className="text-left px-3 py-2">Route</th>
                  <th className="text-right px-3 py-2">Stops</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Staff</th>
                </tr>
              </thead>
              <tbody>
                {metrics.today_plan.routes.map((r, i) => (
                  <tr key={i} className="border-t border-[#F5F2EA]">
                    <td className="px-3 py-2 font-mono">{r.route}</td>
                    <td className="px-3 py-2 text-right font-semibold">{r.stops}</td>
                    <td className="px-3 py-2">{r.vehicle_no || "—"}</td>
                    <td className="px-3 py-2">{r.staff || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No routes scheduled for today yet.</Empty>
        )}
      </Section>

      <Section icon={<Warning size={12}/>} label="AI Risk Alerts" testid="brief-section-risks">
        {(narrative?.risk_bullets?.length || 0) > 0 ? (
          <ul className="list-disc pl-5 text-sm space-y-1">
            {narrative.risk_bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        ) : (
          <Prose>No critical risks flagged by the AI.</Prose>
        )}
        <Grid>
          <Tile label="Vehicles due service (7d)" value={metrics.risk_alerts?.vehicle_maintenance_due_7d} tone="warn"/>
          <Tile label="Tickets overdue (>24h)"     value={metrics.risk_alerts?.tickets_overdue_24h} tone="warn"/>
          <Tile label="High pending payments"      value={inr(metrics.risk_alerts?.high_pending_payments)}/>
          <Tile label="Staff short today?"          value={metrics.risk_alerts?.staff_short ? "Yes" : "No"}
                tone={metrics.risk_alerts?.staff_short ? "warn" : "ok"}/>
        </Grid>
      </Section>

      <Section icon={<Wallet size={12}/>} label="Finance Snapshot" testid="brief-section-finance">
        <Prose>{narrative?.finance_line}</Prose>
        <Grid>
          <Tile label="Yesterday income"        value={inr(metrics.finance?.yesterday_income)} tone="ok"/>
          <Tile label="Yesterday expenses"      value={inr(metrics.finance?.yesterday_expenses)}/>
          <Tile label="Est. profit (yesterday)" value={inr(metrics.finance?.yesterday_profit_estimate)}
                tone={metrics.finance?.yesterday_profit_estimate < 0 ? "warn" : "ok"}/>
          <Tile label="Outstanding dues"        value={inr(metrics.finance?.outstanding_dues)} tone="warn"/>
        </Grid>
      </Section>

      <Section icon={<Trophy size={12}/>} label="Team Recognition" testid="brief-section-recognition">
        <Prose>{narrative?.recognition_line}</Prose>
        {(metrics.recognition?.top_collectors_yesterday?.length || 0) > 0 ? (
          <ol className="list-decimal pl-5 text-sm space-y-1 mt-2">
            {metrics.recognition.top_collectors_yesterday.map((s) => (
              <li key={s.staff_id}>
                <b>{s.name}</b> · {s.collections} completed pickup{s.collections !== 1 ? "s" : ""}
              </li>
            ))}
          </ol>
        ) : (
          <Empty>No completed pickups recorded yesterday.</Empty>
        )}
      </Section>

      <Section icon={<Wrench size={12}/>} label="Improvement Areas" testid="brief-section-improvement">
        {(narrative?.improvement_bullets?.length || 0) > 0 ? (
          <ul className="list-disc pl-5 text-sm space-y-1">
            {narrative.improvement_bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        ) : (
          <Prose>The AI didn&apos;t flag any immediate improvements. Keep monitoring.</Prose>
        )}
      </Section>

      <div className="text-[10px] text-[#8A8780] text-right no-print">
        Generated {new Date(briefing.generated_at).toLocaleString()} ·
        {" "}{briefing.model || "no LLM"}
      </div>
    </div>
  );
}

const Section = ({ icon, label, children, testid }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-2" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] font-semibold">
      <span className="w-6 h-6 rounded-full bg-[#EFF3EE] inline-flex items-center justify-center">{icon}</span>
      {label}
    </div>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">{children}</div>
);

const Tile = ({ label, value, tone }) => {
  const cls = tone === "ok" ? "text-[#2D5A27]"
            : tone === "warn" ? "text-[#7A2E18]"
            : "text-[#1A1A1A]";
  return (
    <div className="border border-[#F0EEE6] rounded-md p-2 bg-[#FAF8F2]">
      <div className="text-[9px] uppercase tracking-[0.14em] text-[#5C5A55]">{label}</div>
      <div className={`text-base font-semibold ${cls}`}>{value ?? "—"}</div>
    </div>
  );
};

const Prose = ({ children }) => (
  children ? <p className="text-sm text-[#1A1A1A] leading-snug">{children}</p> : null
);

const Empty = ({ children }) => (
  <div className="text-[11px] text-[#8A8780] italic py-2">{children}</div>
);
